import React, { useState, useEffect } from 'react';
import { getStats, StatsResponse, getUsageStats, UsageStatsResponse } from '../../api/admin';

const DashboardStats: React.FC = () => {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const [statsData, usageData] = await Promise.all([
        getStats(),
        getUsageStats()
      ]);
      setStats(statsData);
      setUsageStats(usageData);
      setError('');
    } catch (err) {
      setError('Failed to load statistics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-zinc-400">Loading statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        {error}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const subscriptionRate = stats.total_users > 0
    ? ((stats.active_subscriptions / stats.total_users) * 100).toFixed(1)
    : '0';

  const tierData = [
    { name: 'None', count: stats.claims_tiers.none || 0, color: 'bg-zinc-500' },
    { name: 'Free', count: stats.claims_tiers.free || 0, color: 'bg-blue-500' },
    { name: 'Premium', count: stats.claims_tiers.premium || 0, color: 'bg-purple-500' },
  ];

  const maxTierCount = Math.max(...tierData.map(t => t.count), 1);

  return (
    <div>
      <h2 className="text-xl font-bold mb-6 text-zinc-100">Dashboard Overview</h2>

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-label">Total Users</p>
              <p className="stat-card-value text-blue-400">{stats.total_users}</p>
            </div>
            <div className="text-blue-400 opacity-80">
              <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
              </svg>
            </div>
          </div>
          <p className="stat-card-sub">
            {stats.recent_registrations} new in last 7 days
          </p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-label">Active Subscriptions</p>
              <p className="stat-card-value text-emerald-400">{stats.active_subscriptions}</p>
            </div>
            <div className="text-emerald-400 opacity-80">
              <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
          <p className="stat-card-sub">
            {subscriptionRate}% subscription rate
          </p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-label">Admin Users</p>
              <p className="stat-card-value text-amber-400">{stats.admin_users}</p>
            </div>
            <div className="text-amber-400 opacity-80">
              <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>
          <p className="stat-card-sub">
            System administrators
          </p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-label">Premium Tier</p>
              <p className="stat-card-value text-purple-400">{stats.claims_tiers.premium || 0}</p>
            </div>
            <div className="text-purple-400 opacity-80">
              <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
            </div>
          </div>
          <p className="stat-card-sub">
            Premium users
          </p>
        </div>
      </div>

      {/* Claims Tier Distribution */}
      <div className="card-dark rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold mb-4 text-zinc-100">Claims Tier Distribution</h3>
        <div className="space-y-4">
          {tierData.map((tier) => (
            <div key={tier.name}>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-zinc-300">{tier.name}</span>
                <span className="text-sm text-zinc-400">{tier.count} users</span>
              </div>
              <div className="w-full bg-zinc-700/50 rounded-full h-6">
                <div
                  className={`${tier.color} h-6 rounded-full flex items-center justify-center text-white text-xs font-medium transition-all duration-500`}
                  style={{ width: `${Math.max((tier.count / maxTierCount) * 100, tier.count > 0 ? 15 : 0)}%` }}
                >
                  {tier.count > 0 && `${((tier.count / stats.total_users) * 100).toFixed(0)}%`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API Usage Statistics */}
      {usageStats && (
        <>
          <h3 className="text-lg font-semibold mb-4 mt-8 text-zinc-100">API Usage Statistics</h3>

          {/* Usage Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-card-label">Total API Calls</p>
                  <p className="stat-card-value text-indigo-400">{usageStats.total_requests.toLocaleString()}</p>
                </div>
                <div className="text-indigo-400 opacity-80">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
                  </svg>
                </div>
              </div>
              <p className="stat-card-sub">
                {usageStats.requests_today} calls today
              </p>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-card-label">Total Cost</p>
                  <p className="stat-card-value text-rose-400">${usageStats.total_cost.toFixed(2)}</p>
                </div>
                <div className="text-rose-400 opacity-80">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <p className="stat-card-sub">
                ${usageStats.cost_today.toFixed(2)} spent today
              </p>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-card-label">Total Tokens</p>
                  <p className="stat-card-value text-teal-400">{usageStats.total_tokens.toLocaleString()}</p>
                </div>
                <div className="text-teal-400 opacity-80">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <p className="stat-card-sub">
                AI model tokens consumed
              </p>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="stat-card-label">Active Services</p>
                  <p className="stat-card-value text-orange-400">{Object.keys(usageStats.requests_by_service).length}</p>
                </div>
                <div className="text-orange-400 opacity-80">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                  </svg>
                </div>
              </div>
              <p className="stat-card-sub">
                API services in use
              </p>
            </div>
          </div>

          {/* Service Breakdown */}
          <div className="card-dark rounded-xl p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4 text-zinc-100">Usage by Service</h3>
            <div className="overflow-x-auto">
              <table className="table-dark">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Requests</th>
                    <th>Tokens</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(usageStats.requests_by_service).map(([service, count]) => (
                    <tr key={service}>
                      <td>
                        <span className="badge badge-info capitalize">
                          {service}
                        </span>
                      </td>
                      <td className="text-zinc-200">
                        {count.toLocaleString()}
                      </td>
                      <td className="text-zinc-200">
                        {(usageStats.tokens_by_service[service] || 0).toLocaleString()}
                      </td>
                      <td className="font-medium text-zinc-200">
                        ${(usageStats.cost_by_service[service] || 0).toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* System Health */}
      <div className="card-dark rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 text-zinc-100">System Health</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-emerald-500 rounded-full mr-3 shadow-lg shadow-emerald-500/50"></div>
            <span className="text-sm text-zinc-300">Database Connected</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-emerald-500 rounded-full mr-3 shadow-lg shadow-emerald-500/50"></div>
            <span className="text-sm text-zinc-300">API Operational</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-emerald-500 rounded-full mr-3 shadow-lg shadow-emerald-500/50"></div>
            <span className="text-sm text-zinc-300">Email Service Active</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-emerald-500 rounded-full mr-3 shadow-lg shadow-emerald-500/50"></div>
            <span className="text-sm text-zinc-300">Storage Available</span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-zinc-700/50">
          <p className="text-xs text-zinc-500">Last updated: {new Date().toLocaleTimeString()}</p>
          <p className="text-xs text-zinc-500 mt-1">Auto-refresh every 30 seconds</p>
        </div>
      </div>
    </div>
  );
};

export default DashboardStats;
